import React, { forwardRef, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import {
	useNavigationItemAnalyticsWithContext,
	type Analytics,
} from '@atlassian/jira-horizontal-nav-common/src/common/analytics.tsx';
import { V4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import { TAB } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { CONTAINER } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { getNav4Rollout } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-nav4-rollout/index.tsx';
import {
	type Props as PlatformTabListProps,
	TabList as PlatformTabList,
} from '@atlassian/navigation-kit-ui-tab-list';

import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

type Props = {
	addTab?: React.ReactNode;
	id?: string;
	label: string;
	selectedTabId?: string | null;
	tabs: { tab: React.ReactNode; id: string }[];
};

export const TabListWithAnalytics = forwardRef(
	({ addTab, id, label, selectedTabId, tabs }: Props, ref: React.Ref<HTMLDivElement>) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const analytics: Analytics = useMemo(
			() => ({
				itemType: 'more',
				navState: V4,
				stage: getNav4Rollout().stage,
				navigationLayer: CONTAINER,
				navigationContainer: TAB,
				isInMore: false,
			}),
			[],
		);
		const triggerAnalytics = useNavigationItemAnalyticsWithContext(analytics);
		const onMoreClick = useCallback(
			({ overflowingTabCount }: { overflowingTabCount: number }) => {
				triggerAnalytics({ childCount: overflowingTabCount });
			},
			[triggerAnalytics],
		);
		const onMoreContentChanged = useMemo(
			() =>
				debounce<NonNullable<PlatformTabListProps['onMoreContentChanged']>>(
					({ overflowingTabIds, visibleTabIds }) => {
						fireTrackAnalytics(createAnalyticsEvent({}), 'horizontalNavJiraTabsContent changed', {
							hiddenTabIds: overflowingTabIds,
							visibleTabIds,
						});
					},
					300,
				),
			[createAnalyticsEvent],
		);

		return (
			<PlatformTabList
				addTab={addTab}
				id={id}
				label={label}
				onMoreClick={onMoreClick}
				onMoreContentChanged={onMoreContentChanged}
				ref={ref}
				selectedTabId={selectedTabId}
				tabs={tabs}
			/>
		);
	},
);
